import React from 'react';
import { Col, Pagination, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import usePagination from 'hooks/usePagination';
import {
  faAngleRight,
  faChevronLeft,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import Button from 'components/base/Button';
import { useAdvanceTableContext } from 'providers/AdvanceTableProvider';
import { t } from 'i18next';
import ConditionalRender from 'base/ConditionalRender/ConditionalRender';
import { dataPerPageSelectOptions } from 'utils/staticData';

interface AdvanceTableFooterProps {
  className?: string;
  pagination?: boolean;
  navBtn?: boolean;
  showViewAllBtn?: boolean;
  handleNextPage: () => void;
  handlePreviousPage: () => void;
  totalPages: number;
  totalRecords: number;
  recordsPerPage: number;
  CanPreviousPage: () => boolean;
  CanNextPage: () => boolean;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  isAllVisibleData?: boolean;
  setIsAllVisibleData?: (value: boolean) => void;
  setPageSize?: (value: number) => void;
  showDataPerPage?: boolean;
  setRecordsPerPage: (value: number) => void;
  isLoading: boolean;
}

const CustomAdvanceTableFooter: React.FC<AdvanceTableFooterProps> = ({
  className,
  pagination,
  handleNextPage,
  handlePreviousPage,
  totalRecords,
  recordsPerPage,
  CanPreviousPage,
  currentPage,
  CanNextPage,
  setCurrentPage,
  isAllVisibleData = false,
  setIsAllVisibleData = () => {},
  showViewAllBtn = true,
  showDataPerPage,
  setRecordsPerPage,
  isLoading
}) => {
  const getPageCount = () => {
    return Math.ceil(totalRecords / recordsPerPage);
  };

  const { setPageSize } = useAdvanceTableContext();

  const { hasNextEllipsis, hasPrevEllipsis, visiblePaginationItems } =
    usePagination({
      currentPageNo: currentPage,
      totalPage: getPageCount(),
      maxPaginationButtonCount: 5
    });

  let endsWith;
  if (recordsPerPage * currentPage < totalRecords) {
    endsWith = recordsPerPage * currentPage;
  } else {
    endsWith = totalRecords;
  }

  const pageSizeOptions = dataPerPageSelectOptions.filter(
    (pageSize: number) => pageSize <= totalRecords
  );

  const getStartPage = (): number => {
    if (isAllVisibleData) {
      if (totalRecords > 0) {
        return 1;
      } else {
        return 0;
      }
    } else {
      if (totalRecords <= 0) {
        return 0;
      } else {
        return recordsPerPage * (currentPage - 1) + 1;
      }
    }
  };

  return (
    <Row className={classNames(className, 'align-items-center py-1')}>
      <ConditionalRender condition={!isLoading}>
        <Col className="d-flex fs-9 align-items-center gap-2">
          <p className="mb-0 d-none d-sm-block me-3 fw-semibold text-body">
            {getStartPage()} to {isAllVisibleData ? totalRecords : endsWith}
            <span className="text-body-tertiary"> items of </span>
            {totalRecords}
          </p>
          {showViewAllBtn &&
            totalRecords !== recordsPerPage &&
            totalRecords > recordsPerPage && (
              <Button
                variant="link"
                className="p-0 fw-semibold"
                endIcon={
                  <FontAwesomeIcon icon={faAngleRight} className="ms-1 fs-9" />
                }
                onClick={() => {
                  if (setIsAllVisibleData) {
                    setIsAllVisibleData(!isAllVisibleData);
                    setPageSize(
                      isAllVisibleData ? recordsPerPage : totalRecords
                    );
                  }
                }}
              >
                {t('general.common_text.text_1')}{' '}
                {isAllVisibleData
                  ? t('common_table.text_3')
                  : t('common_table.text_2')}
              </Button>
            )}
          <ConditionalRender condition={!!showDataPerPage && !isAllVisibleData}>
            <select
              value={recordsPerPage}
              onChange={e => setRecordsPerPage(Number(e.target.value))}
              className="form-select w-auto"
            >
              {pageSizeOptions.map(pageSize => (
                <option key={pageSize} value={pageSize}>
                  {pageSize}
                </option>
              ))}
            </select>
          </ConditionalRender>
        </Col>

        {pagination && (
          <Col xs="auto">
            <Pagination className="mb-0 justify-content-center">
              {!isAllVisibleData ? (
                <>
                  <Pagination.Prev
                    disabled={!CanPreviousPage()}
                    onClick={handlePreviousPage}
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </Pagination.Prev>
                </>
              ) : (
                <>
                  <Pagination.Prev disabled>
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </Pagination.Prev>
                </>
              )}

              {hasPrevEllipsis && (
                <>
                  <Pagination.Item
                    active={isAllVisibleData ? true : currentPage === 1}
                    onClick={() => setCurrentPage(1)}
                  >
                    1
                  </Pagination.Item>
                  <Pagination.Ellipsis disabled />
                </>
              )}

              <>
                {!isAllVisibleData ? (
                  visiblePaginationItems.map(page => (
                    <Pagination.Item
                      key={page}
                      active={currentPage === page}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </Pagination.Item>
                  ))
                ) : (
                  <>
                    <Pagination.Item
                      active={true}
                      onClick={() => setCurrentPage(1)}
                      disabled={!!isAllVisibleData}
                    >
                      1
                    </Pagination.Item>
                  </>
                )}
              </>

              {hasNextEllipsis && (
                <>
                  <Pagination.Ellipsis disabled />
                  <Pagination.Item
                    active={
                      isAllVisibleData ? true : currentPage === getPageCount()
                    }
                    onClick={() => setCurrentPage(getPageCount())}
                    disabled={!!isAllVisibleData}
                  >
                    {getPageCount()}
                  </Pagination.Item>
                </>
              )}
              {!isAllVisibleData ? (
                <>
                  <Pagination.Next
                    disabled={!CanNextPage()}
                    onClick={handleNextPage}
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </Pagination.Next>
                </>
              ) : (
                <>
                  <Pagination.Next disabled>
                    <FontAwesomeIcon icon={faChevronRight} />
                  </Pagination.Next>
                </>
              )}
            </Pagination>
          </Col>
        )}
      </ConditionalRender>
    </Row>
  );
};

export default CustomAdvanceTableFooter;
